import { TH, el } from './theme';
import { WEAPONS, WEAPON_IDS } from '../game/weapons';
import { weaponFitAll, setWeaponFit, saveWeaponFit, type WeaponFitEntry, type V3 } from '../engine/weaponFit';

// F4 -> Objects -> GUNS: per-gun fit calibration (owner tool). Every slider applies LIVE to the equipped
// weapon (main re-equips the FP viewmodel + 3P hand on each change), so the workflow is: equip the gun
// in-game, open this panel, drag until it looks right at hip + ADS, check the muzzle flash sits on the
// barrel tip, then SAVE (writes public/data/weapon_fit.json through the dev middleware).

// OWNER 2026-07-11: F8 tunes EVERY handheld, not just guns -- all weapons + the tools/utility items.
export const GUN_IDS: string[] = ['ak', ...WEAPON_IDS,
  'hammer', 'blueprint', 'torch', 'rock', 'guidebook', 'bandage', 'chest', 'bigchest',
  'wood_axe', 'stone_axe', 'metal_axe', 'wood_pickaxe', 'stone_pickaxe', 'metal_pickaxe'];

type FitKey = 'fp' | 'rot' | 'ads' | 'adsRot' | 'muzzle' | 'p3' | 'p3r' | 'p3Aim' | 'p3rAim' | 'scl';
const GROUPS: Array<{ key: FitKey; label: string; hint: string; min: number; max: number; step: number; view: ViewFilter }> = [
  { key: 'scl', label: 'Scale (W / H / L)', hint: 'per-axis size multiplier -- x width, y height, z length (1 = as modelled)', min: 0.2, max: 3, step: 0.01, view: 'all' },
  { key: 'muzzle', label: 'Muzzle point', hint: 'flash + tracer origin (must sit on THIS gun\'s barrel tip)', min: -2, max: 2, step: 0.01, view: 'all' },
  { key: 'fp', label: 'FP position (hip)', hint: 'where the model sits in the hand (x right, y up, z forward-)', min: -1.5, max: 1.5, step: 0.005, view: 'fp' },
  { key: 'rot', label: 'FP rotation (hip)', hint: 'radians, on top of the base orientation', min: -Math.PI, max: Math.PI, step: 0.01, view: 'fp' },
  { key: 'ads', label: 'FP position (ADS)', hint: 'position delta at full aim, independent of the hip seat above', min: -0.6, max: 0.6, step: 0.005, view: 'fpads' },
  { key: 'adsRot', label: 'FP rotation (ADS)', hint: 'radians at full aim, independent of the hip rotation above', min: -Math.PI, max: Math.PI, step: 0.01, view: 'fpads' },
  { key: 'p3', label: '3P position (rest)', hint: 'third-person seat on the player model', min: -1, max: 1, step: 0.005, view: 'tp' },
  { key: 'p3r', label: '3P rotation (rest)', hint: 'radians -- the 3P hand frame is NOT the 1P frame, tune separately', min: -Math.PI, max: Math.PI, step: 0.01, view: 'tp' },
  { key: 'p3Aim', label: '3P position (aim)', hint: 'ABSOLUTE seat while aiming -- starts from the rest tune, then fully independent', min: -1, max: 1, step: 0.005, view: 'tpaim' },
  { key: 'p3rAim', label: '3P rotation (aim)', hint: 'ABSOLUTE radians while aiming -- starts from the rest tune, then fully independent', min: -Math.PI, max: Math.PI, step: 0.01, view: 'tpaim' },
];

function defaults(id: string, key: string): V3 {
  const twoHanded = id === 'ak' || (WEAPONS[id as keyof typeof WEAPONS]?.twoHanded ?? false);
  if (key === 'fp') return twoHanded ? [0, 0.47, -0.3] : [0, 0.15, -0.18];
  if (key === 'muzzle') return twoHanded ? [0, 0.96, -0.82] : [0, 0.64, -0.44];
  if (key === 'scl') return [1, 1, 1];
  return [0, 0, 0];
}

// Which view a group belongs to; 'all' groups (scale/muzzle -- physical properties, not per-view poses)
// always show. F8's four view-lock buttons ('fp'|'fpads'|'tp'|'tpaim') filter to JUST that view's own
// fields, so it's obvious which saved config you're editing; 'free' (or omitted, e.g. the F4 tab) shows
// everything at once.
export type ViewFilter = 'all' | 'fp' | 'fpads' | 'tp' | 'tpaim';

// Does this item HAVE aim poses? Guns + bows enter ADS/shouldered-aim; tools/melee/torch/etc never do,
// so for them the ADS/AIM views alias to hip/rest -- the panel must never show sliders that do nothing.
export function itemAims(id: string): boolean {
  if (id === 'ak') return true;
  const k = WEAPONS[id as keyof typeof WEAPONS]?.kind;
  return k === 'gun' || k === 'bow';
}

export function renderGunLab(host: HTMLElement, gunId: string, onDirty: () => void, view: ViewFilter | 'free' = 'free'): void {
  const fit: WeaponFitEntry = weaponFitAll()[gunId] ?? {};
  const wrap = el('div', `display:flex;flex-direction:column;gap:${TH.s3};max-width:640px`);

  // Non-aiming items (pickaxes, torch, melee...): 1st ADS === 1st, 3rd AIM === 3rd. Alias the view so the
  // sliders shown ALWAYS drive the held model (owner 2026-07-12: dead ADS sliders on a pickaxe).
  const aims = itemAims(gunId);
  const effView: ViewFilter | 'free' = !aims && view === 'fpads' ? 'fp' : !aims && view === 'tpaim' ? 'tp' : view;
  if (effView !== view) {
    wrap.append(el('div', `font:700 ${TH.f2} system-ui;color:#d9a23a;line-height:1.4`,
      `This item has no aim pose — tuning its ${effView === 'fp' ? '1st person' : '3rd person'} config (shared with the non-aim view).`));
  }

  const note = el('div', `font:${TH.f2} system-ui;color:${TH.dim};line-height:1.5`,
    `Equip the ${WEAPONS[gunId as keyof typeof WEAPONS]?.name ?? gunId.toUpperCase()} in-game first — every slider applies LIVE to the held weapon (hip + hold right-mouse to check ADS). Each view (1st, 1st ADS, 3rd, 3rd AIM) saves its own independent config. Save writes weapon_fit.json.`);
  wrap.append(note);

  const shown = effView === 'free' ? GROUPS.filter((g) => aims || (g.view !== 'fpads' && g.view !== 'tpaim'))
    : GROUPS.filter((g) => g.view === 'all' || g.view === effView);
  for (const g of shown) {
    // The AIM view's absolute fields fall back to the REST tune at runtime -- seed the sliders the same
    // way, so the first drag continues from the pose you're looking at instead of snapping to zero.
    const fallback: V3 | undefined = g.key === 'p3Aim' ? (fit.p3 as V3 | undefined) : g.key === 'p3rAim' ? (fit.p3r as V3 | undefined) : undefined;
    const cur: V3 = (fit[g.key] as V3 | undefined) ?? fallback ?? defaults(gunId, g.key);
    const box = el('div', `border:1px solid ${TH.line};border-radius:${TH.r};background:${TH.panel};padding:${TH.s3} ${TH.s4}`);
    const head = el('div', `display:flex;align-items:baseline;gap:${TH.s3};margin-bottom:6px`);
    head.append(
      el('div', `font:700 ${TH.f3} system-ui;color:${TH.text}`, g.label),
      el('div', `font:${TH.f1} system-ui;color:${TH.faint}`, g.hint),
    );
    const reset = el('button', `margin-left:auto;font:${TH.f1} system-ui;color:${TH.dim};background:none;border:1px solid ${TH.line};border-radius:4px;padding:2px 8px;cursor:pointer`, 'reset');
    reset.addEventListener('click', () => { setWeaponFit(gunId, { [g.key]: undefined } as Partial<WeaponFitEntry>); onDirty(); renderGunLab(host, gunId, onDirty, view); });
    head.append(reset);
    box.append(head);
    (['x', 'y', 'z'] as const).forEach((axis, ai) => {
      const row = el('div', 'display:flex;align-items:center;gap:10px;padding:3px 0');
      const lab = el('div', `width:14px;font:${TH.f2} ui-monospace,monospace;color:${TH.dim}`, axis);
      const slider = document.createElement('input');
      slider.type = 'range'; slider.min = String(g.min); slider.max = String(g.max); slider.step = String(g.step);
      slider.value = String(cur[ai]);
      slider.style.cssText = 'flex:1';
      const num = document.createElement('input');
      num.type = 'number'; num.step = String(g.step); num.value = cur[ai].toFixed(3);
      num.style.cssText = `width:78px;background:${TH.bg2};border:1px solid ${TH.line};border-radius:4px;color:${TH.text};font:${TH.f2} ui-monospace,monospace;padding:3px 6px`;
      const apply = (v: number): void => {
        cur[ai] = v;
        setWeaponFit(gunId, { [g.key]: [...cur] as V3 } as Partial<WeaponFitEntry>);
        onDirty();
      };
      slider.addEventListener('input', () => { num.value = Number(slider.value).toFixed(3); apply(Number(slider.value)); });
      num.addEventListener('change', () => { slider.value = num.value; apply(Number(num.value)); });
      row.append(lab, slider, num);
      box.append(row);
    });
    wrap.append(box);
  }

  // OVERALL SCALE: one slider that multiplies the whole model uniformly (on top of the per-axis group).
  {
    const box = el('div', `border:1px solid ${TH.line};border-radius:${TH.r};background:${TH.panel};padding:${TH.s3} ${TH.s4}`);
    const head = el('div', `display:flex;align-items:baseline;gap:${TH.s3};margin-bottom:6px`);
    head.append(el('div', `font:700 ${TH.f3} system-ui;color:${TH.text}`, 'Overall scale'),
      el('div', `font:${TH.f1} system-ui;color:${TH.faint}`, 'uniform size multiplier (1 = as modelled)'));
    const reset = el('button', `margin-left:auto;font:${TH.f1} system-ui;color:${TH.dim};background:none;border:1px solid ${TH.line};border-radius:4px;padding:2px 8px;cursor:pointer`, 'reset');
    reset.addEventListener('click', () => { setWeaponFit(gunId, { scale: undefined }); onDirty(); renderGunLab(host, gunId, onDirty, view); });
    head.append(reset);
    box.append(head);
    const row = el('div', 'display:flex;align-items:center;gap:10px;padding:3px 0');
    const slider = document.createElement('input');
    slider.type = 'range'; slider.min = '0.2'; slider.max = '3'; slider.step = '0.01';
    slider.value = String(fit.scale ?? 1);
    slider.style.cssText = 'flex:1';
    const num = document.createElement('input');
    num.type = 'number'; num.step = '0.01'; num.value = (fit.scale ?? 1).toFixed(2);
    num.style.cssText = `width:78px;background:${TH.bg2};border:1px solid ${TH.line};border-radius:4px;color:${TH.text};font:${TH.f2} ui-monospace,monospace;padding:3px 6px`;
    const apply = (v: number): void => { setWeaponFit(gunId, { scale: v }); onDirty(); };
    slider.addEventListener('input', () => { num.value = Number(slider.value).toFixed(2); apply(Number(slider.value)); });
    num.addEventListener('change', () => { slider.value = num.value; apply(Number(num.value)); });
    row.append(slider, num);
    box.append(row);
    wrap.append(box);
  }

  const saveRow = el('div', `display:flex;gap:${TH.s3};align-items:center`);
  const save = el('button', `padding:9px 22px;border-radius:${TH.rsm};border:none;background:#2f7a3f;color:#fff;font:700 ${TH.f3} system-ui;cursor:pointer`, 'Save weapon_fit.json');
  const status = el('div', `font:${TH.f2} system-ui;color:${TH.dim}`, '');
  save.addEventListener('click', () => {
    void saveWeaponFit().then((ok) => { status.textContent = ok ? 'Saved ✓' : 'Save failed (dev server only)'; status.style.color = ok ? '#9be0a0' : '#e06a6a'; });
  });
  saveRow.append(save, status);
  wrap.append(saveRow);

  host.replaceChildren(wrap);
}
